import React, { useState, useEffect } from 'react';
import adminService from '../../services/adminService';

const ManageSettings = () => {
  const [settings, setSettings] = useState({
    pool_fee: '50.00',
    prize_first: '70',
    prize_second: '20',
    prize_third: '10',
    pix_key: '',
    pix_merchant_name: '',
    pix_merchant_city: '',
    scoring_exact_score: '3',
    scoring_correct_outcome: '1',
    scoring_knockout_draw_predicted_draw: '1',
    scoring_knockout_draw_exact_score: '1',
    scoring_knockout_draw_correct_winner: '1'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSettings();
      
      const newSettings = { ...settings };
      data.forEach(item => {
        if (newSettings[item.key] !== undefined) {
          newSettings[item.key] = item.value;
        }
      });
      
      setSettings(newSettings);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações.' });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate percentages
    const totalPct = parseFloat(settings.prize_first) + parseFloat(settings.prize_second) + parseFloat(settings.prize_third);
    if (totalPct !== 100) {
      setMessage({ type: 'error', text: `A soma das porcentagens deve ser 100%. Atual: ${totalPct}%` });
      return;
    }

    // Validate scoring rules: non-negative integers
    const scoringKeys = [
      'scoring_exact_score',
      'scoring_correct_outcome',
      'scoring_knockout_draw_predicted_draw',
      'scoring_knockout_draw_exact_score',
      'scoring_knockout_draw_correct_winner'
    ];
    for (const key of scoringKeys) {
      const n = Number(settings[key]);
      if (!Number.isInteger(n) || n < 0) {
        setMessage({ type: 'error', text: 'Pontuações devem ser números inteiros não-negativos.' });
        return;
      }
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const updates = Object.keys(settings).map(key => ({
        key,
        value: settings[key]
      }));

      await adminService.updateSettings(updates);
      
      setMessage({ type: 'success', text: 'Configurações atualizadas com sucesso!' });
      setSaving(false);
    } catch (error) {
      console.error('Error updating settings:', error);
      setMessage({ type: 'error', text: 'Erro ao atualizar configurações.' });
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center p-8">Carregando configurações...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-primary">Configurações do Bolão</h1>
      
      {message.text && (
        <div className={`p-4 mb-6 rounded-md ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl">
        <form onSubmit={handleSubmit}>
          
          <h2 className="text-lg font-semibold mb-4 border-b pb-2">Valores</h2>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pool_fee">
              Valor da Inscrição por Usuário (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              id="pool_fee"
              name="pool_fee"
              value={settings.pool_fee}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
            <p className="text-sm text-gray-500 mt-1">Este valor será multiplicado pelo número de pagamentos confirmados para calcular o prêmio total.</p>
          </div>

          <h2 className="text-lg font-semibold mb-4 border-b pb-2">Distribuição dos Prêmios (%)</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="prize_first">
                1º Lugar (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                id="prize_first"
                name="prize_first"
                value={settings.prize_first}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="prize_second">
                2º Lugar (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                id="prize_second"
                name="prize_second"
                value={settings.prize_second}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="prize_third">
                3º Lugar (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                id="prize_third"
                name="prize_third"
                value={settings.prize_third}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
          </div>
          
          <h2 className="text-lg font-semibold mb-4 border-b pb-2">Dados do PIX (Recebimento)</h2>

          <p className="text-sm text-gray-500 mb-4">
            Estes dados são usados para gerar o QR Code PIX que os participantes escaneiam ao pagar a inscrição.
          </p>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pix_key">
              Chave PIX
            </label>
            <input
              type="text"
              id="pix_key"
              name="pix_key"
              value={settings.pix_key}
              onChange={handleChange}
              placeholder="email@exemplo.com, CPF, telefone ou chave aleatória"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pix_merchant_name">
                Nome do Beneficiário
              </label>
              <input
                type="text"
                id="pix_merchant_name"
                name="pix_merchant_name"
                value={settings.pix_merchant_name}
                onChange={handleChange}
                maxLength={25}
                placeholder="Máx. 25 caracteres"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pix_merchant_city">
                Cidade do Beneficiário
              </label>
              <input
                type="text"
                id="pix_merchant_city"
                name="pix_merchant_city"
                value={settings.pix_merchant_city}
                onChange={handleChange}
                maxLength={15}
                placeholder="Máx. 15 caracteres"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
          </div>

          <h2 className="text-lg font-semibold mb-4 border-b pb-2">Pontuação</h2>

          <p className="text-sm text-gray-500 mb-4">
            Valores aplicados retroativamente em todas as partidas finalizadas (o ranking é recalculado a cada acesso).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="scoring_exact_score">
                Placar exato
              </label>
              <input
                type="number"
                step="1"
                min="0"
                id="scoring_exact_score"
                name="scoring_exact_score"
                value={settings.scoring_exact_score}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="scoring_correct_outcome">
                Resultado correto (V/E/D)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                id="scoring_correct_outcome"
                name="scoring_correct_outcome"
                value={settings.scoring_correct_outcome}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-3 mt-4">
            <strong>Eliminatória com empate</strong> — pontos somados (precisa ter previsto empate):
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="scoring_knockout_draw_predicted_draw">
                Previu empate
              </label>
              <input
                type="number"
                step="1"
                min="0"
                id="scoring_knockout_draw_predicted_draw"
                name="scoring_knockout_draw_predicted_draw"
                value={settings.scoring_knockout_draw_predicted_draw}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="scoring_knockout_draw_exact_score">
                Placar exato (+)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                id="scoring_knockout_draw_exact_score"
                name="scoring_knockout_draw_exact_score"
                value={settings.scoring_knockout_draw_exact_score}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="scoring_knockout_draw_correct_winner">
                Acertou quem avança (+)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                id="scoring_knockout_draw_correct_winner"
                name="scoring_knockout_draw_correct_winner"
                value={settings.scoring_knockout_draw_correct_winner}
                onChange={handleChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-end mt-8">
            <button
              type="submit"
              disabled={saving}
              className={`bg-tertiary hover:bg-tertiary-dark text-white font-bold py-2 px-6 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:shadow-outline ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageSettings;
